import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BellRing, Bot, Check, Database, HardDrive, Plug, Upload, Zap } from "lucide-react";
import { API_CONFIG, apiFetch } from "@/services/api";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/services/SettingsContext";
import { getMetricsDataSource, setMetricsDataSource, type MetricsDataSource } from "@/services/MetricsService";
import { getYfilesRenderer, setYfilesRenderer, type YfilesRenderer } from "@/services/YfilesRendererService";
import { getTeamLabel, setTeamLabel, type TeamLabel } from "@/services/TeamLabelService";
import { enableCapture, isCachingEnabled, setCachingEnabled, hasAnyResponse } from "@/services/CacheService";

import { useToast } from "@/hooks/use-toast";

const HEALTH_ALERTS_DISMISSED_KEY = "health-alerts-dismissed-ids";

export const SettingsSection: React.FC = () => {
  const { apiSettings, updateApiSettings } = useSettings();
  const [dismissedCount, setDismissedCount] = useState<number>(0);
  const [metricsDataSource, setDataSource] = useState<MetricsDataSource>(getMetricsDataSource());
  const [yfilesRenderer, setRenderer] = useState<YfilesRenderer>(getYfilesRenderer());
  const [teamLabel, setTeamLabelState] = useState<TeamLabel>(getTeamLabel());
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [useCachedData, setUseCachedDataState] = useState<boolean>(isCachingEnabled);
  const [hasCachedDataAvailable, setHasCachedDataAvailable] = useState<boolean>(hasAnyResponse);
  const { toast } = useToast();

  useEffect(() => {
    loadDismissedCount();
  }, []);

  const loadDismissedCount = () => {
    try {
      const stored = localStorage.getItem(HEALTH_ALERTS_DISMISSED_KEY);
      if (stored) {
        const ids = JSON.parse(stored);
        if (Array.isArray(ids)) {
          setDismissedCount(ids.length);
        }
      } else {
        setDismissedCount(0);
      }
    } catch (e) {
      console.error("Failed to read dismissed alerts", e);
      setDismissedCount(0);
    }
  };

  const handleResetNotifications = () => {
    try {
      localStorage.removeItem(HEALTH_ALERTS_DISMISSED_KEY);
      setDismissedCount(0);
      toast({
        title: "Notifications reset",
        description: "All dismissed health alerts have been restored.",
        duration: 3000,
      });
      
      // Dispatch a storage event so other components (like useHealthAlerts) might pick it up if they listen to it
      // Note: storage event only fires on other tabs/windows usually, but we might want to manually trigger re-evaluation if needed.
      // For now, removing the item is enough as the hook reads on mount or update. 
      // To force the hook to re-read, a reload might be necessary if it doesn't poll.
      // Looking at useHealthAlerts, it reads on mount. We might need to force a reload or suggest it.
      // But the requirement says "user will be able to see and reset".
    } catch (e) {
      console.error("Failed to reset notifications", e);
      toast({
        title: "Error",
        description: "Failed to reset notifications.",
        variant: "destructive",
      });
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file && !file.name.match(/^timeseries-(\w+)\.json$/)) {
      toast({ title: 'Invalid file name', description: 'File must be named timeseries-{aggregation}.json', variant: 'destructive' });
      setSelectedFile(null);
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
  };

  const handleUploadFile = async () => {
    if (!selectedFile) return;

    const match = selectedFile.name.match(/^timeseries-(\w+)\.json$/);
    if (!match) {
      toast({ title: 'Invalid file name', description: 'File must be named timeseries-{aggregation}.json', variant: 'destructive' });
      return;
    }
    const aggregation = match[1];

    // Read instance (environment) and model (selectedModelLabel) from sessionStorage apiSettings
    let instance = '';
    let model = '';
    try {
      const savedSettings = sessionStorage.getItem('apiSettings');
      if (savedSettings) {
        const apiSettings = JSON.parse(savedSettings);
        instance = apiSettings.environment || '';
        model = apiSettings.selectedModelLabel || '';
      }
    } catch {
      // ignore parse errors
    }

    if (!instance || !model) {
      toast({ title: 'Missing configuration', description: 'Instance (environment) and model (selectedModelLabel) must be set in API settings.', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const content = await selectedFile.text();
      const payload = JSON.parse(content);

      const params = new URLSearchParams({ aggregation, instance, model });
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.metricsTimeseriesBlob}?${params.toString()}`;

      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(err.error || `Upload failed with status ${response.status}`);
      }

      toast({ title: 'Upload successful', description: `Uploaded ${selectedFile.name} to ${instance}/${model}/`, duration: 4000 });
      setSelectedFile(null);
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message || 'An error occurred during upload.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your application preferences</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Upload className="h-5 w-5" />
            <CardTitle>Upload Timeseries Data</CardTitle>
          </div>
          <CardDescription>
            Upload a JSON file to blob storage. File must be named timeseries-{'{aggregation}'}.json (e.g. timeseries-daily.json).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg border p-4">
            <Input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              className="flex-1"
            />
            <Button variant="outline" onClick={handleUploadFile} disabled={!selectedFile || uploading}>
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
          {selectedFile && (
            <p className="text-sm text-muted-foreground px-4">
              Ready: <span className="font-medium">{selectedFile.name}</span>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Plug className="h-5 w-5" />
            <CardTitle>Prismatic API</CardTitle>
          </div>
          <CardDescription>
            Configure the Prismatic API connection used for live timeseries data.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 rounded-lg border p-4">
            <Label htmlFor="prismaticApiUrl">Prismatic API URL</Label>
            <Input
              id="prismaticApiUrl"
              type="url"
              autoComplete="off"
              value={apiSettings.prismaticApiUrl ?? ''}
              onChange={(e) => updateApiSettings({ prismaticApiUrl: e.target.value })}
              placeholder="https://hooks.eu-west-2.prismatic.io/trigger/..."
            />
            <Label htmlFor="prismaticApiKey" className="mt-2">Prismatic API Key</Label>
            <Input
              id="prismaticApiKey"
              type="password"
              autoComplete="off"
              value={apiSettings.prismaticApiKey ?? ''}
              onChange={(e) => updateApiSettings({ prismaticApiKey: e.target.value })}
              placeholder="Enter your API key"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <BellRing className="h-5 w-5" />
            <CardTitle>Notification Settings</CardTitle>
          </div>
          <CardDescription>
            Manage how alerts and notifications are displayed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <h3 className="text-base font-medium">Dismissed Health Alerts</h3>
              <p className="text-sm text-muted-foreground">
                You have dismissed {dismissedCount} health alert{dismissedCount !== 1 ? 's' : ''}.
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleResetNotifications}
              disabled={dismissedCount === 0}
            >
              {dismissedCount === 0 ? <Check className="mr-2 h-4 w-4" /> : null}
              Reset Dismissed Alerts
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Database className="h-5 w-5" />
            <CardTitle>Metrics Settings</CardTitle>
          </div>
          <CardDescription>
            Configure how metrics data is fetched.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <h3 className="text-base font-medium">Data Source</h3>
              <p className="text-sm text-muted-foreground">
                {metricsDataSource === 'api' ? 'Live data' : 'Simulated data'}
              </p>
            </div>
            <Switch
              checked={metricsDataSource === 'blob'}
              onCheckedChange={(checked) => {
                const source: MetricsDataSource = checked ? 'blob' : 'api';
                setDataSource(source);
                setMetricsDataSource(source);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Zap className="h-5 w-5" />
            <CardTitle>Graph Rendering</CardTitle>
          </div>
          <CardDescription>
            Configure how the value stream navigator renders its graph.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <h3 className="text-base font-medium">Rendering Engine</h3>
              <p className="text-sm text-muted-foreground">
                {yfilesRenderer === 'webgl'
                  ? 'WebGL enabled — faster load, reduced visual fidelity. Reload the navigator to apply.'
                  : 'Standard SVG rendering with full visual fidelity.'}
              </p>
            </div>
            <Switch
              checked={yfilesRenderer === 'webgl'}
              onCheckedChange={(checked) => {
                const renderer: YfilesRenderer = checked ? 'webgl' : 'svg';
                setRenderer(renderer);
                setYfilesRenderer(renderer);
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <HardDrive className="h-5 w-5" />
            <CardTitle>API Response Caching</CardTitle>
          </div>
          <CardDescription>
            Capture live API responses to local storage and optionally serve them instead of making real requests.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 rounded-lg border p-4">
            <div className="space-y-0.5 flex-1">
              <h3 className="text-base font-medium">Cache API response</h3>
              <p className="text-sm text-muted-foreground">
                Arms capture for value streams, value stream details and metrics. Redirects to the home page where requests are made.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setCachingEnabled(false);
                enableCapture();
                window.location.href = '/';
              }}
            >
              Cache API response
            </Button>
          </div>
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <h3 className="text-base font-medium">Use cached data</h3>
              <p className="text-sm text-muted-foreground">
                {useCachedData ? 'Serving data from local cache instead of live API.' : 'Live API responses are being used.'}
              </p>
            </div>
            <span title={!hasCachedDataAvailable ? 'No cached data available' : undefined}>
              <Switch
                checked={useCachedData}
                disabled={!hasCachedDataAvailable}
                onCheckedChange={(checked) => {
                  setCachingEnabled(checked);
                  setUseCachedDataState(checked);
                  setHasCachedDataAvailable(hasAnyResponse());
                }}
              />
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Bot className="h-5 w-5" />
            <CardTitle>Substitute Label</CardTitle>
          </div>
          <CardDescription>
            Substitute the team node label and icon in the value stream navigator.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <h3 className="text-base font-medium">Team / Agent</h3>
              <p className="text-sm text-muted-foreground">
                {teamLabel === 'Agent'
                  ? 'Agent mode — team nodes show the cube icon and \'Agent\' label. Reload the navigator to apply.'
                  : 'Standard mode — team nodes show the team icon and \'Team\' label.'}
              </p>
            </div>
            <Switch
              checked={teamLabel === 'Agent'}
              onCheckedChange={(checked) => {
                const label: TeamLabel = checked ? 'Agent' : 'Team';
                setTeamLabelState(label);
                setTeamLabel(label);
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
